<template>
    <div class="envelope-workspace">
        <envelope-title
            :read_only="read_only"
            v-model="envelope_title"
            :envelope="local_envelope"
            :creating="creating"
        >
        </envelope-title>

        <div class="grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
        <section class="envelope-panel envelope-settings">
        <envelope-recipients
            :read_only="read_only"
            :property="property"
            :envelope="local_envelope"
            :broker_will_sign="broker_will_sign"
            :statuses="recipient_statuses"
            :toast="toast_target === 'recipients' || toast_target === 'broker_will_sign' ? 'Saved' : null"
            @recipientsUpdated="handleRecipientsUpdated"
            @brokerWillSignUpdated="handleBrokerWillSignUpdated"
        />

        </section>
        <envelope-documents
            :read_only="read_only"
            :property_id="property.id"
            :property="property"
            :documents="documents"
            :templates="templates"
            :document_folders="document_folders"
            :envelope="local_envelope"
            :envelope_documents="envelope_documents"
            :toast="toast_target === 'documents' ? 'Saved' : null"
            @documents-updated="handleDocumentsUpdated"
        />


        </div>

        <div class="envelope-status-area">
        <envelope-status
            v-if="local_envelope && (! creating)"
            :envelope="local_envelope"
            :envelopes_available="envelopes_available"
            :signature_purchase_route="signature_purchase_route"
            :read_only="read_only"
            :has_documents="has_documents"
            :has_recipients="recipients_are_valid"
            @launchDocusign="launchDocusign"
            @recipientStatusUpdated="updateRecipientStatus"
        />

        <div v-if="creating && !read_only"
            class="envelope-actions"
        >
            <div class="flex flex-wrap flex-1 justify-end items-center gap-3">
                <button type="button" class="envelope-button" @click="saveEnvelope">
                    <span >Save Changes</span>
                </button>

                <button type="button" class="envelope-button envelope-primary" @click="handleDocusignClick"
                    :disabled="! has_documents || ! recipients_are_valid"
                >
                    <span >Send for Signatures</span>
                </button>
            </div>
        </div>

        <div v-else-if="!read_only"
            class="envelope-actions"
        >
            <div v-if="! local_envelope.completed_at"
                class="flex flex-wrap flex-1 justify-end items-center gap-3"
            >
                <button type="button" class="envelope-button envelope-delete" v-if="editable"
                    @click="show_delete_confirmation = true"
                >
                    <i class="far fa-trash-alt mr-1"></i>
                    Delete
                </button>

                <button type="button" class="envelope-button envelope-primary" @click="handleDocusignClick"
                    :disabled="! has_documents || ! recipients_are_valid"
                >
                    <span >{{ editable ? 'Send for Signatures' : 'View on Docusign' }}</span>
                </button>
            </div>

            <div v-if="local_envelope.completed_at"
                class="flex flex-wrap flex-1 justify-end items-center gap-3"
            >
                <button type="button" class="envelope-button envelope-primary" @click="handleDocusignClick"
                    :disabled="! has_documents || ! recipients_are_valid"
                >
                    <span >View on Docusign</span>
                </button>
            </div>
        </div>

        </div>

        <docusign-loading-modal
            :show="loading_docusign"
        />

        <envelope-delete-modal
            v-if="envelope"
            :show="show_delete_confirmation"
            :modal_name="`envelope-delete-modal-${envelope.id}`"
            @deleteEnvelope="deleteEnvelope"
            @closed="show_delete_confirmation = false"
        />

        <broker-completion-required-modal
            v-if="envelope"
            :show="show_broker_completion_modal"
            @closed="show_broker_completion_modal = false"
            @launchDocusign="launchDocusign"
        />

        <envelope-has-template-modal
            :show="show_template_modal"
            @closed="show_template_modal = false"
            @launchDocusign="launchDocusign"
        />
    </div>
</template>

<script>
import BrokerCompletionRequiredModal from './broker-completion-required-modal.vue';
import ButtonBlue from '../../../components/button-blue.vue';
import ButtonBlueOutline from '../../../components/button-blue-outline.vue';
import ButtonGreen from '../../../components/button-green.vue';
import ButtonTextRed from '../../../components/button-text-red.vue';
import DocusignLoadingModal from './docusign-loading-modal.vue';
import EnvelopeDeleteModal from './envelope-delete-modal.vue';
import EnvelopeDocuments from './envelope-documents.vue';
import EnvelopeHasTemplateModal from './envelope-has-template-modal.vue';
import EnvelopeRecipients from './envelope-recipients.vue';
import EnvelopeStatus from './envelope-status.vue';
import EnvelopeTitle from './envelope-title.vue';
import Debounce from '../../../mixins/debounce.js';

export default {
    components: {
        BrokerCompletionRequiredModal,
        ButtonBlue,
        ButtonBlueOutline,
        ButtonGreen,
        ButtonTextRed,
        DocusignLoadingModal,
        EnvelopeDeleteModal,
        EnvelopeDocuments,
        EnvelopeHasTemplateModal,
        EnvelopeRecipients,
        EnvelopeStatus,
        EnvelopeTitle,
    },

    mixins: [
        Debounce,
    ],

    props: {
        read_only: { default: false },
        property: {},
        documents: {},
        templates: {},
        document_folders: {},
        envelope: {},
        envelope_count: {},
        envelopes_available: {},
        signature_purchase_route: {},
        signatures_create_route: {},
        signatures_index_route: {},
        signatures_destroy_route: {},
        signatures_view_docusign_route: {},
        prompt_for_broker_completion: {},
    },

    data() {
        return {
            local_envelope: this.envelope,
            envelope_title: this.envelope?.title || this.getDefaultTitle(),
            envelope_documents: this.envelope?.property_documents || [],
            envelope_recipients: this.envelope?.recipients || [],
            broker_will_sign: this.property.broker
                ? (this.envelope ? this.envelope.broker_will_sign : true)
                : false,
            loading_docusign: false,
            recipient_statuses: [],
            has_unsaved_changes: false,
            toast_target: null,
            show_delete_confirmation: false,
            show_broker_completion_modal: false,
            show_template_modal: false,
        }
    },

    computed: {
        creating() {
            return ! this.local_envelope;
        },
        local_envelope_id() {
            return this.local_envelope?.id || null;
        },
        editable() {
            return !this.read_only && ! this.local_envelope?.sent_at;
        },
        has_documents() {
            return this.envelope_documents?.length > 0;
        },
        has_recipients() {
            return !! this.envelope_recipients?.filter(recipient => recipient.name || recipient.email).length;
        },
        has_templates() {
            return this.envelope_documents?.some(document => document.type === 'template');
        },
        recipients_are_valid() {
            return this.has_recipients
                && this.envelope_recipients
                .every(recipient => recipient.name && this.emailIsValid(recipient.email));
        },
    },

    methods: {
        getDefaultTitle() {
            return 'envelope #' + (parseInt(this.envelope_count) + 1);
        },
        emailIsValid(email) {
            return String(email)
                .toLowerCase()
                .match(
                  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                );
        },
        saveEnvelope(redirect = true, autosaved_section = null) {
            if (this.read_only || this.local_envelope?.sent_at) return Promise.resolve();
            if (! this.local_envelope) {
                return this.createEnvelope(redirect);
            }

            let payload = {
                title: this.envelope_title || this.getDefaultTitle(),
                documents: this.envelope_documents,
                recipients: this.envelope_recipients,
                broker_will_sign: this.broker_will_sign,
            };

            if (autosaved_section) {
                // Limit to one key from payload, depending on which section is being updated.
                payload = {[autosaved_section]: payload[autosaved_section]};
            }

            return axios.patch(this.signatures_create_route + '/' + this.local_envelope_id, payload)
                .then(response => {
                    if (redirect) {
                        window.location.href = this.signatures_index_route;
                    }

                    if (autosaved_section) {
                        this.showToast(autosaved_section);
                    }
                }).catch(error => {
                    if (error.response.data.error) {
                        alert(error.response.data.error);
                    }
                }).finally(() => {
                    this.has_unsaved_changes = false;
                });
        },
        createEnvelope(redirect = true) {
            return axios.post(this.signatures_create_route, {
                title: this.envelope_title || this.getDefaultTitle(),
                documents: this.envelope_documents,
                recipients: this.envelope_recipients,
                broker_will_sign: this.broker_will_sign,
            }).then(response => {
                this.local_envelope = response.data;

                if (redirect) {
                    window.location.href = this.signatures_index_route;
                }
            }).catch(error => {
                if (error.response.data.error) {
                    alert(error.response.data.error);
                }
            });
        },
        deleteEnvelope() {
            this.show_delete_confirmation = false;

            if (! this.local_envelope) {
                return;
            }

            const destroyUrl = this.signatures_destroy_route.replace('ENVELOPE_ID', this.local_envelope_id);

            return axios.delete(destroyUrl)
                .then(response => {
                    window.location.href = this.signatures_index_route;
                }).catch(error => {
                    if (error.response?.data?.error) {
                        alert(error.response.data.error);
                    } else if (error.response?.status === 400) {
                        alert('Cannot delete an envelope that has been sent.');
                    } else {
                        alert('An error occurred while deleting the envelope.');
                    }
                });
        },
        handleDocusignClick() {
            if (this.editable && this.has_templates) {
                this.showTemplateModal();
            } else {
                this.launchDocusign();
            }
        },
        showTemplateModal() {
            this.show_template_modal = true;
        },
        launchDocusign() {
            // Show loading modal immediately
            this.loading_docusign = true;
            
            this.saveEnvelope(false).then(() => {
                // Make sure we have a valid envelope ID
                if (! this.local_envelope_id) {
                    this.loading_docusign = false;
                    alert('Failed to create envelope. Please try again.');
                    return;
                }

                // Construct the Docusign URL route
                let docusignRoute;

                if (this.signatures_view_docusign_route) {
                    docusignRoute = this.signatures_view_docusign_route;
                } else {
                    // For new envelopes, construct the route manually
                    const baseUrl = this.signatures_create_route.split('/signatures')[0];
                    docusignRoute = `${baseUrl}/signatures/${this.local_envelope_id}/docusign-url`;
                }

                axios.post(docusignRoute)
                    .then(response => {
                        if (response.data?.docusign_url) {
                            window.location.href = response.data.docusign_url;
                        } else {
                            this.loading_docusign = false;
                            console.log(response);
                        }
                    }).catch(error => {
                        this.loading_docusign = false;

                        if (error.response?.data?.error) {
                            alert(error.response.data.error);
                        } else {
                            console.log(error);
                        }
                    });
            }).catch(error => {
                console.error('Failed to save envelope:', error);
                alert('Failed to save envelope. Please try again.');
            });
        },
        showToast(target) {
            this.toast_target = target;

            setTimeout(() => {
                this.toast_target = null;
            }, 1700)
        },
        updateRecipientStatus(statuses) {
            this.recipient_statuses = statuses;
        },
        handleDocumentsUpdated(documents) {
            this.envelope_documents = documents;
            this.has_unsaved_changes = true;

            // Auto-save document changes when updating
            if (this.local_envelope && ! this.creating) {
                this.saveEnvelope(false, 'documents').catch(error => {
                    console.error('Failed to auto-save: ', error);
                });
            }
        },
        handleRecipientsUpdated(recipients) {
            this.envelope_recipients = recipients;
            this.has_unsaved_changes = true;

            // Auto-save recipient changes when updating
            if (this.local_envelope && ! this.creating) {
                this.saveEnvelope(false, 'recipients').catch(error => {
                    console.error('Failed to auto-save: ', error);
                });
            }
        },
        handleBrokerWillSignUpdated(broker_will_sign) {
            this.broker_will_sign = broker_will_sign;
            this.has_unsaved_changes = true;

            // Auto-save recipient changes when updating
            if (this.local_envelope && ! this.creating) {
                this.saveEnvelope(false, 'broker_will_sign').catch(error => {
                    console.error('Failed to auto-save: ', error);
                });
            }
        },
        handleTitleUpdated(title) {
            // Auto-save title changes when updating
            if (this.local_envelope && ! this.creating) {
                this.saveEnvelope(false, 'title').catch(error => {
                    console.error('Failed to auto-save: ', error);
                });
            }
        },
        cancelChanges() {
            window.location.reload();
        },
    },

    created() {
        this.handleTitleUpdated = this.debounce(this.handleTitleUpdated, 500);
        this.handleRecipientsUpdated = this.debounce(this.handleRecipientsUpdated, 500);

        if (this.envelope && this.prompt_for_broker_completion) {
            this.show_broker_completion_modal = true;

            // Remove the alert trigger so it doesn't recur on reload
            const url = new URL(window.location.href);
            const params = new URLSearchParams(url.search);
            params.delete('needs_broker_completion');

            url.search = params.toString();
            window.history.replaceState({}, '', url);
        }
    },

    watch: {
        envelope_title (value, oldValue) {
            this.has_unsaved_changes = true;
            this.handleTitleUpdated();
        },
    },
}
</script>

<style>
.envelope-workspace { --envelope-panel-height: 28rem; }
.envelope-workspace .envelope-settings { height: var(--envelope-panel-height); }
.envelope-workspace .envelope-status-area { display: flex; align-items: center; flex-wrap: wrap; gap: 1rem 3rem; margin-top: 1.5rem; }
.envelope-workspace .envelope-status-area .envelope-status-summary { flex: 1 1 20rem; min-width: 0; }
.envelope-workspace .envelope-status-area .envelope-actions { margin-left: auto; padding: 0; flex-shrink: 0; }
.envelope-workspace .envelope-panel { border: 1px solid #d1d5db; border-radius: 0.5rem; background: white; min-width: 0; overflow: hidden; }
.envelope-workspace .envelope-section { position: relative; padding: 1rem; border-bottom: 1px solid #e5e7eb; }
.envelope-workspace .envelope-section-heading { font-size: 1rem; font-weight: 600; color: #374151; margin: -1rem -1rem 1rem; padding: 0.625rem 1rem; background: #f9fafb; border-bottom: 1px solid #e5e7eb; }
.envelope-workspace .envelope-button { display: inline-flex; align-items: center; justify-content: center; gap: 0.5rem; border: 1px solid #d1d5db; border-radius: 0.375rem; padding: 0.35rem 0.65rem; font-size: 0.8125rem; font-weight: 400; line-height: 1.5; background: white; color: #374151; }
.envelope-workspace .envelope-button:hover { background: #f9fafb; }
.envelope-workspace .envelope-signers-toolbar .envelope-button:hover:not(:disabled),
.envelope-workspace .envelope-documents-toolbar .envelope-button:hover:not(:disabled) { border-color: #2563eb; color: #2563eb; background: white; }
.envelope-workspace .envelope-primary { background: #2563eb; border-color: #2563eb; color: white; }
.envelope-workspace .envelope-primary:hover { background: #1d4ed8; }
.envelope-workspace .envelope-delete { color: #6b7280; }
.envelope-workspace .envelope-delete:hover { color: #b91c1c; border-color: #b91c1c; background: white; }
.envelope-edit-modal .envelope-button,
.envelope-workspace .envelope-actions .envelope-button,
.envelope-workspace .envelope-signers-toolbar .envelope-button,
.envelope-workspace .envelope-documents-toolbar .envelope-button { padding: 0.55rem 0.9rem; font-size: 0.875rem; }
.envelope-workspace .envelope-button:disabled { opacity: 0.5; cursor: default; }
.envelope-workspace .envelope-actions { display: flex; justify-content: flex-end; padding: 0.75rem 1rem; }
.envelope-workspace .field { font-size: 0.875rem; border-radius: 0.375rem; }
.envelope-workspace .envelope-section p { font-size: 0.875rem; }
</style>
